import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { EmptyState } from '@ecom/ui-kit';
import { useCallback, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import {
  useJibexAccountQuery,
  useJibexChecksQuery,
  useSaveJibexAccountMutation,
  useTestJibexMutation,
} from '../api';
import { JibexAccountForm } from '../components/JibexAccountForm';
import { JibexCheckHistory } from '../components/JibexCheckHistory';
import { JibexNotificationsCard } from '../components/JibexNotificationsCard';
import { JibexStatusCard } from '../components/JibexStatusCard';
import { toSaveBody } from '../schema';
import { OUTCOME_LABEL_KEY } from '../state';
import type { JibexAccountFormValues } from '../types';

import { FormSkeleton } from '@/components/ui/skeletons/FormSkeleton';
import { useToast } from '@/components/ui/toast/ToastProvider';
import { useActiveShop } from '@/features/shops/use-active-shop';
import { ApiError } from '@/lib/api-client';

/**
 * « Compte Jibex » (Lot L5a) — the merchant connects their own Jibex account: client id, secret, a test, the state and
 * the history of checks.
 *
 * OWNER ONLY. The server refuses everyone else on every route (`@RequireRole('merchant_owner')`); this page also says
 * so up front for a Staff session instead of letting them hit a 403, and does not even fetch for them.
 *
 * THE SECRET never lives in this component's state: the form hands the typed values to `onSave`, which builds the
 * request body and sends it; the mutation is collected and reset as soon as it settles (see `api.ts`).
 */
export function JibexAccountPage(): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const shop = useActiveShop();
  const isOwner = shop.activeMembership?.roleKey === 'merchant_owner';

  const accountQuery = useJibexAccountQuery(isOwner);
  const checksQuery = useJibexChecksQuery(isOwner);
  const save = useSaveJibexAccountMutation();
  const test = useTestJibexMutation();

  const [formIsDirty, setFormIsDirty] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const handleDirtyChange = useCallback((dirty: boolean) => {
    setFormIsDirty(dirty);
  }, []);

  /** A limit or an outage is told to the owner with the wait, not as a generic « forbidden ». */
  function describeFailure(error: unknown, whenLimited: 'test' | 'save'): string {
    if (error instanceof ApiError && error.status === 429) {
      return t('merchant.delivery.account.test.tooMany', {
        seconds: error.retryAfterSeconds ?? 30,
      });
    }
    if (error instanceof ApiError && error.status === 503) {
      return t('merchant.delivery.account.test.unavailable');
    }
    if (error instanceof ApiError && error.status === 404 && whenLimited === 'test') {
      return t('merchant.delivery.account.test.saveFirst');
    }
    return t(resolveErrorMessageKey(error));
  }

  async function onSave(values: JibexAccountFormValues): Promise<void> {
    setFormError(null);
    setNotice(null);
    try {
      await save.mutateAsync(toSaveBody(values));
      toast.success(t('merchant.delivery.account.form.saved'));
    } catch (error) {
      setFormError(describeFailure(error, 'save'));
    } finally {
      // The mutation's variables held the secret: drop them now.
      save.reset();
    }
  }

  async function onTest(): Promise<void> {
    setNotice(null);
    try {
      const result = await test.mutateAsync();
      const result_ = t(OUTCOME_LABEL_KEY[result.outcome]);
      const message = t('merchant.delivery.account.test.done', { result: result_ });
      if (result.outcome === 'connected') toast.success(message);
      else toast.error(message);
    } catch (error) {
      setNotice(describeFailure(error, 'test'));
    }
  }

  if (shop.isLoading) {
    return (
      <div className="mx-auto max-w-3xl">
        <FormSkeleton fields={4} />
      </div>
    );
  }

  const header = (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="sk-page-title">{t('merchant.delivery.account.title')}</h1>
        <p className="text-sm text-inkFaint">{t('merchant.delivery.account.subtitle')}</p>
      </div>
      <Link to="/delivery" className="text-sm font-medium text-accent hover:underline">
        {t('merchant.delivery.account.back')}
      </Link>
    </div>
  );

  if (!isOwner) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col gap-6">
        {header}
        <EmptyState
          title={t('merchant.common.accessDenied')}
          description={t('merchant.delivery.account.ownerOnly')}
        />
      </div>
    );
  }

  if (accountQuery.isPending) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col gap-6">
        {header}
        <FormSkeleton fields={4} />
      </div>
    );
  }

  if (accountQuery.isError) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col gap-6">
        {header}
        <EmptyState
          title={
            accountQuery.error.status === 403
              ? t('merchant.common.accessDenied')
              : t('merchant.delivery.account.loadError')
          }
          description={
            accountQuery.error.status === 403
              ? t('merchant.delivery.account.ownerOnly')
              : t(resolveErrorMessageKey(accountQuery.error))
          }
        />
      </div>
    );
  }

  const view = accountQuery.data;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      {header}

      <JibexStatusCard
        view={view}
        formIsDirty={formIsDirty}
        testing={test.isPending}
        notice={notice}
        onTest={() => {
          void onTest();
        }}
      />

      {formError !== null ? (
        <p role="alert" className="rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical">
          {formError}
        </p>
      ) : null}

      <JibexAccountForm
        view={view}
        submitting={save.isPending}
        onSubmit={onSave}
        onDirtyChange={handleDirtyChange}
      />

      <JibexNotificationsCard />

      <aside className="rounded-md border border-line bg-surface p-4">
        <h2 className="text-sm font-semibold text-ink">
          {t('merchant.delivery.account.security.title')}
        </h2>
        <p className="mt-1 text-sm text-inkSoft">{t('merchant.delivery.account.security.body')}</p>
      </aside>

      {checksQuery.isError ? (
        <EmptyState
          title={t('merchant.delivery.account.history.loadError')}
          description={t(resolveErrorMessageKey(checksQuery.error))}
        />
      ) : (
        <JibexCheckHistory checks={checksQuery.data ?? []} />
      )}
    </div>
  );
}
